import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import SelectImportMethod from './SelectImportMethod';
import ImportWaiting from './ImportWaiting';
import ImportSuccess from './ImportSuccess';
import useMetaMask from '../../../../utils/useMetaMask';
import { ChainEnum, CoinType, ImportMethod } from '../../../../enum';
import ImportFail from './ImportFail';
import LedgerPath from './LedgerPath';
import LedgerSdk from '../../../../utils/ledger-sdk';
import { useGlobalState } from '../../../../store';
import Cyano from '../../../../utils/DApp/Cyano';

type Props = {
  onCancel: () => void;
  onSuccess: () => void;
}

enum Step {
  Method,
  Wait,
  Success,
  Fail,
  LedgerPathInput
}

const AddWalletAsset: React.FC<Props> = ({ onCancel, onSuccess }) => {
  const [dApp] = useGlobalState('dApp');
  const setAddressList = useGlobalState('addressList')[1];
  const [step, setStep] = useState<Step>(Step.Method);
  const [selectedMethod, setSelectedMethod] = useState<ImportMethod | ''>('');
  const { getAddress, getSignature, getPublicKey } = useMetaMask(dApp ? dApp.ontId : '');
  const setScoreLoading = useGlobalState('scoreLoading')[1];
  const { t } = useTranslation();

  const selectMethodHandler = async (method: ImportMethod) => {
    setSelectedMethod(method);
    if (method === ImportMethod.MetaMask) {
      setStep(Step.Wait);
      try {
        const addressResult = await getAddress(t);
        if (addressResult && dApp) {
          const signResult = await getSignature(dApp.ontId, addressResult);
          setTimeout(async () => { // metamask need to be active twice
            try {
              const publicKey = await getPublicKey(addressResult);
              await (dApp as Cyano).addAddress2Cyano({
                address: addressResult,
                chain: ChainEnum.ETH_CHAIN,
                pubkey: publicKey,
                sig: signResult,
              });
              await (dApp as Cyano).loadAddressList();
              setScoreLoading(true);
              dApp.fetchScore().finally(() => {
                setScoreLoading(false);
              });
              setAddressList(dApp.addressList);
              setStep(Step.Success);
            } catch (e) {
              setStep(Step.Fail);
              message.error(e.message);
            }
          }, 500);
        } else {
          setStep(Step.Fail);
        }
      } catch (e) {
        setStep(Step.Fail);
        message.error(e.message || t('add_wallet_fail'));
      }
    } else if (method === ImportMethod.Ledger) {
      setStep(Step.LedgerPathInput);
    }
  };

  const handleLedgerPathConfirm = async (coinType: CoinType, ledgerPath: string) => {
    setStep(Step.Wait);
    let sdkType = '';
    switch (coinType) {
      case CoinType.BTC:
        sdkType = 'BTC';
        break;
      case CoinType.ETH:
        sdkType = 'ETH';
        break;
      default:
        // no default
    }
    try {
      const sdk = await LedgerSdk.create(sdkType);
      const result = await sdk.getAddressAndPublicKey(ledgerPath);
      const signResult = await sdk.signMessage({
        path: ledgerPath,
        message: (dApp as Cyano).ontId,
      });
      await (dApp as Cyano).addAddress2Cyano({
        address: result.address,
        chain: coinType,
        sig: signResult,
        pubkey: result.publicKey,
      });
      await (dApp as Cyano).loadAddressList();
      setStep(Step.Success);
    } catch (e) {
      setStep(Step.Fail);
      message.error(e.message || t('add_wallet_fail'));
    }
  };

  if (step === Step.Method) {
    return (
      <SelectImportMethod onNext={selectMethodHandler} />
    );
  }
  if (step === Step.Wait) {
    return <ImportWaiting />;
  }
  if (step === Step.Success) {
    return <ImportSuccess onConfirm={onSuccess} />;
  }
  if (step === Step.Fail) {
    return (
      <ImportFail
        onReTry={() => selectMethodHandler(selectedMethod as ImportMethod)}
        onCancel={onCancel}
      />
    );
  }
  if (step === Step.LedgerPathInput) {
    return (
      <LedgerPath onCancel={onCancel} onConfirm={handleLedgerPathConfirm} />
    );
  }
  return null;
};

export default AddWalletAsset;
